/**
 * Sign-in. Two modes:
 * - `oidc`: Microsoft Entra External ID (Google federated there), authorization code + PKCE with oidc-client-ts.
 *   The access token goes to the API as `Authorization: Bearer …`.
 * - `dev`: the backend's AUTH_MODE=dev verifier: `X-Dev-User: <email>` (+ `X-Dev-Platform-Admin: 1`), chosen on a
 *   small sign-in screen and kept in localStorage. Never available against a production backend.
 */
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { config } from "../config";

const DEV_KEY = "solaros.devUser";

export interface DevUser {
  email: string;
  platformAdmin: boolean;
}

export interface SignInOptions {
  signup?: boolean;
  returnTo?: string;
  /** Prefills the email at the identity provider. */
  loginHint?: string;
  /** Company SSO: External ID sends the user straight to the company's identity provider for this domain. */
  domainHint?: string;
  /** Company SSO: sign in through this app registration (the SSO user flow's; lib/ssoClient.ts), not the
   * dashboard's own, whose flow never holds company federations. */
  ssoClientId?: string | null;
}

interface AuthState {
  mode: "oidc" | "dev";
  ready: boolean;
  signedIn: boolean;
  email: string | null;
  name: string | null;
  signIn: (options?: SignInOptions) => Promise<void>;
  devSignIn: (user: DevUser) => void;
  signOut: () => Promise<void>;
}

/** The app registration of the current sign-in (localStorage): the SSO app's after a company sign-in. A sign-in
 * that is under way keeps its app in sessionStorage until its callback succeeds. */
const CLIENT_KEY = "solaros.authClient";
const PENDING_KEY = "solaros.authClient.pending";

function stored(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function store(storage: () => Storage, key: string, value: string | null) {
  try {
    if (value) storage().setItem(key, value);
    else storage().removeItem(key);
  } catch {
    /* private mode */
  }
}

const managers = new Map<string, UserManager>();
function managerFor(clientId: string): UserManager {
  let manager = managers.get(clientId);
  if (!manager) {
    const origin = window.location.origin;
    manager = new UserManager({
      authority: config.authAuthority,
      client_id: clientId,
      redirect_uri: `${origin}/auth/callback`,
      post_logout_redirect_uri: `${origin}/login`,
      response_type: "code",
      scope: ["openid", "profile", "email", "offline_access", config.authScope].filter(Boolean).join(" "),
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      loadUserInfo: false,
    });
    managers.set(clientId, manager);
  }
  return manager;
}

/** The UserManager of the current sign-in: the dashboard's app, or the SSO app after a company sign-in. */
export function userManager(): UserManager {
  return managerFor(stored(() => localStorage, CLIENT_KEY) || config.authClientId);
}

/** The callback of a sign-in: handled by the app that started it, which becomes the current one. */
export async function completeSignIn(): Promise<User> {
  const clientId = stored(() => sessionStorage, PENDING_KEY) || stored(() => localStorage, CLIENT_KEY) || config.authClientId;
  const user = await managerFor(clientId).signinRedirectCallback();
  store(() => localStorage, CLIENT_KEY, clientId === config.authClientId ? null : clientId);
  store(() => sessionStorage, PENDING_KEY, null);
  return user;
}

function readDevUser(): DevUser | null {
  try {
    const raw = localStorage.getItem(DEV_KEY);
    return raw ? (JSON.parse(raw) as DevUser) : null;
  } catch {
    return null;
  }
}

/** Headers that authenticate an API request right now. */
export async function authHeaders(): Promise<Record<string, string>> {
  if (config.authMode === "dev") {
    const dev = readDevUser();
    if (!dev) return {};
    return { "X-Dev-User": dev.email, ...(dev.platformAdmin ? { "X-Dev-Platform-Admin": "1" } : {}) };
  }
  const user = await userManager().getUser();
  return user && !user.expired ? { Authorization: `Bearer ${user.access_token}` } : {};
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [oidcUser, setOidcUser] = useState<User | null>(null);
  const [devUser, setDevUser] = useState<DevUser | null>(() => (config.authMode === "dev" ? readDevUser() : null));
  const [ready, setReady] = useState(config.authMode === "dev");

  useEffect(() => {
    if (config.authMode !== "oidc") return;
    const um = userManager();
    um.getUser().then((u) => {
      setOidcUser(u && !u.expired ? u : null);
      setReady(true);
    });
    const loaded = (u: User) => setOidcUser(u);
    const unloaded = () => setOidcUser(null);
    um.events.addUserLoaded(loaded);
    um.events.addUserUnloaded(unloaded);
    um.events.addSilentRenewError(unloaded);
    return () => {
      um.events.removeUserLoaded(loaded);
      um.events.removeUserUnloaded(unloaded);
      um.events.removeSilentRenewError(unloaded);
    };
  }, []);

  const signIn = useCallback(async (options?: SignInOptions) => {
    if (config.authMode !== "oidc") return;
    const clientId = options?.ssoClientId || config.authClientId;
    store(() => sessionStorage, PENDING_KEY, clientId);
    await managerFor(clientId).signinRedirect({
      state: { returnTo: options?.returnTo ?? "/" },
      // Entra External ID shows the sign-up page with prompt=create.
      ...(options?.signup ? { prompt: "create" } : {}),
      ...(options?.loginHint ? { login_hint: options.loginHint } : {}),
      ...(options?.domainHint ? { extraQueryParams: { domain_hint: options.domainHint } } : {}),
      // the identity provider's pages in the language on screen (the landing site's ?locale=, the user's choice)
      ...(document.documentElement.lang ? { ui_locales: document.documentElement.lang } : {}),
    });
  }, []);

  const devSignIn = useCallback((user: DevUser) => {
    localStorage.setItem(DEV_KEY, JSON.stringify(user));
    setDevUser(user);
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem("solaros.org");
    if (config.authMode === "dev") {
      localStorage.removeItem(DEV_KEY);
      setDevUser(null);
      return;
    }
    const manager = userManager();
    store(() => localStorage, CLIENT_KEY, null);
    await manager.signoutRedirect();
  }, []);

  const value = useMemo<AuthState>(() => {
    const profile = oidcUser?.profile;
    return {
      mode: config.authMode,
      ready,
      signedIn: config.authMode === "dev" ? !!devUser : !!oidcUser,
      email: config.authMode === "dev" ? devUser?.email ?? null : (profile?.email as string | undefined) ?? null,
      name: config.authMode === "dev" ? null : (profile?.name as string | undefined) ?? null,
      signIn,
      devSignIn,
      signOut,
    };
  }, [oidcUser, devUser, ready, signIn, devSignIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
